import React from 'react'
import { ParamTemplateRequirement, StepControls } from '@/features/templates'
import Autocomplete from '@/components/ui/Autocomplete'
import { useField, FieldArray, useFormikContext } from 'formik'
import Typography from '@/components/ui/Typography'
import { capitalCase } from 'change-case'
import Button from '@/components/ui/Buttons/Button'
import DeleteButton from '@/components/ui/Buttons/DeleteButton'
import { curryAddNewView, useViewOptions } from './SlackNotificationsViewsConfigStep.hooks'
import FormikInput from '@/components/formik/FormikInput'
import FormLabel from '@/components/ui/FormLabel'
import classes from './SlackNotificationsViewsConfigStep.module.scss'
import Link from '@/components/ui/Link'
import { Links } from '@/config/app'

export type SlackNotificationsViewsConfigStepProps = {
  name: string
  requirement: ParamTemplateRequirement
}

export const SlackNotificationsViewsConfigStep = ({ name, requirement }: SlackNotificationsViewsConfigStepProps) => {
  const { setFieldValue } = useFormikContext()
  const [, { value }, { setValue }] = useField<object[] | undefined>(name)
  const viewOptions = useViewOptions()

  const onChange = async (fieldName: string, newValue: string) => {
    setFieldValue(fieldName, newValue)
  }

  return (
    <div className={'d-flex flex-column gap-2'}>
      <div>
        <Typography variant={'xxl'}>{capitalCase(name)}</Typography>
        <Typography>{requirement.description}</Typography>
      </div>

      <FieldArray name={name}>
        {(helpers) => (
          <>
            {value?.map((view, i) => (
              <div key={i} className="d-flex flex-column gap-2">
                <div className="d-flex gap-2">
                  <div style={{ flex: 1 }}>
                    <FormLabel>View</FormLabel>
                    <Autocomplete name={`${name}.${i}.view`} value={value} onChange={onChange} options={viewOptions} />
                  </div>

                  <DeleteButton onClick={() => helpers.remove(i)} className={classes.removeButton} />
                </div>
                <div className="d-flex gap-2">
                  <FormikInput
                    name={`${name}.${i}.slackUrl`}
                    label="Slack URL"
                    description={
                      <Typography>
                        Create app in Slack and use URL from it. See more info{' '}
                        <Link isExternal href={Links.slackNotificationsWorkflowGuide}>
                          here
                        </Link>
                        .
                      </Typography>
                    }
                  />
                  <FormikInput
                    name={`${name}.${i}.threshold`}
                    label="Threshold"
                    description="How many tickets should be in the view to notify Slack"
                    type="number"
                    min={1}
                    step={1}
                  />
                </div>
              </div>
            ))}

            <Button onClick={curryAddNewView(helpers)} variant="outlined" color="neutral">
              Add view
            </Button>
          </>
        )}
      </FieldArray>
      <StepControls disabled={value?.length === 0} />
    </div>
  )
}
